import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AttributeType, FeatureDc, LayerReferenceConfigurationDc, PagedListFeatureDc } from "@evergis/api";
import { CircularProgress, ComparisonOperator, InfiniteScroll } from "@evergis/ui";
import { useDebouncedCallback } from "@evergis/react";

import { ProgressContainer } from "../../../LayerList/components/LayerListSearch/styled";
import { AttributeLabel, Main, ValueLink } from "../../styled";
import { RecordItem, RecordsList } from "./styled";
import { api } from "../../../../evergis/api";
import { getAppConfig } from "../../../../ducks/appConfig";
import { getSearchString, getSourceLayerInfo } from "../../../../ducks/showFeature/selectors";
import { getRelatedAttribute } from "../../utils/getRelatedAttribute";
import { setPreviousFeatureReference } from "../../../../ducks/showFeature/showFeature";
import { formatAttributeValue } from "../../../../utils/formatAttributeValue";
import { useShowFeatureCard } from "../../../Table/hooks/useShowFeatureCard";
import { usePostgresLayer } from "../../../../hooks/layer/usePostgresLayer";
import { FeatureAttribute } from "./FeatureAttributeField";
import { FilterCondition } from "../../../../evergis/core/condition/FilterCondition";
import { PreviousFeatureReference } from "../../../../ducks/showFeature/types";
import { FilterConditionOperation } from "../../../../components/LayerFilter/types";
import { FeatureDcExtended } from "../../../../ducks/selectFeatures/types";

export interface HandBookRecordsProps {
  layerReference: LayerReferenceConfigurationDc;
  attributes: FeatureAttribute[];
  feature: FeatureDcExtended;
  previousFeatureReference?: PreviousFeatureReference;
}

const DEFAULT_LIMIT = 40;
const LOADING_DEBOUNCE_TIME = 500;

export const HandBookRecords: FC<HandBookRecordsProps> = ({
  layerReference,
  attributes,
  feature,
  previousFeatureReference,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const { settings } = useSelector(getAppConfig);
  const searchString = useSelector(getSearchString);
  const sourceLayerInfo = useSelector(getSourceLayerInfo);
  const [items, setItems] = useState<FeatureDc[]>([]);
  const [currentPage, setCurrentPage] = useState<PagedListFeatureDc>();
  const { postgresLayer: referenceLayer } = usePostgresLayer(layerReference?.layerName);

  const relatedAttribute = getRelatedAttribute(
    sourceLayerInfo,
    layerReference.targetAttribute,
    layerReference.layerName,
  );

  const referenceTitleAttribute = referenceLayer?.layerDefinition?.titleAttribute;
  const titleAttribute = relatedAttribute?.attributeTitle || referenceTitleAttribute;
  const referenceAttributesConfiguration = referenceLayer?.layerDefinition?.attributes;
  const referenceAttributeDefinition =
    referenceAttributesConfiguration && referenceAttributesConfiguration[layerReference?.referenceAttribute];
  const referenceTitleAttributeDefinition =
    referenceAttributesConfiguration && referenceAttributesConfiguration[titleAttribute];

  const showFeature = useShowFeatureCard(layerReference?.layerName);
  const loadDebounce = useDebouncedCallback(LOADING_DEBOUNCE_TIME);

  const loadNext = useCallback(() => {
    loadDebounce(async () => {
      const limit = settings?.layer?.maxHandbookRecords || DEFAULT_LIMIT;
      const targetAttribute = attributes.find(({ name }) => name === layerReference.targetAttribute);

      const newPage = await api.layers.getFeatures(layerReference?.layerName, {
        offset: currentPage ? currentPage?.offset + limit : 0,
        limit,
        attributes: [titleAttribute],
        withGeom: false,
        query: FilterCondition.from(
          [
            {
              attribute: layerReference?.referenceAttribute,
              comparisonOperator: ComparisonOperator.Equals,
              value: targetAttribute.value as string,
              type: targetAttribute.type,
            },
            searchString
              ? {
                  attribute: titleAttribute,
                  comparisonOperator: ComparisonOperator.Equals,
                  value: `%${searchString}%`,
                  type: AttributeType.String,
                }
              : null,
          ].filter(Boolean),
          FilterConditionOperation.AND,
        ).toString(),
      });

      setCurrentPage(newPage);
      setItems(currentItems => [...currentItems, ...newPage.items]);
    });
  }, [
    loadDebounce,
    settings?.layer?.maxHandbookRecords,
    attributes,
    layerReference?.layerName,
    layerReference?.referenceAttribute,
    layerReference.targetAttribute,
    currentPage,
    titleAttribute,
    searchString,
  ]);

  const onClick = useCallback(
    (id: string) => {
      showFeature(id);

      dispatch(
        setPreviousFeatureReference({
          id: feature.id,
          layer: feature.layer,
          reference: layerReference,
          previous: previousFeatureReference,
        }),
      );
    },
    [dispatch, feature, layerReference, previousFeatureReference, showFeature],
  );

  useEffect(() => {
    setItems([]);
    setCurrentPage(void 0);
  }, [feature?.id, feature?.layer, searchString]);

  return (
    <Main padding={"1.5rem 1rem 1.5rem 1.5rem"}>
      <AttributeLabel>{t("relatedRecords")}</AttributeLabel>

      <InfiniteScroll
        hasMore={!currentPage || currentPage?.totalCount > currentPage?.offset + currentPage?.limit}
        loader={
          <ProgressContainer>
            <CircularProgress diameter={1.25} />
          </ProgressContainer>
        }
        loadNext={loadNext}
      >
        <RecordsList>
          {items.map(({ id, attributes: featureAttributes }) =>
            featureAttributes ? (
              <RecordItem key={id} onClick={() => onClick(id)}>
                <ValueLink as={"div"}>
                  {(featureAttributes[titleAttribute] &&
                    formatAttributeValue(
                      referenceTitleAttributeDefinition.type,
                      featureAttributes[titleAttribute],
                      referenceTitleAttributeDefinition.stringFormat,
                    )) ||
                    formatAttributeValue(
                      referenceAttributeDefinition.type,
                      featureAttributes[layerReference.referenceAttribute],
                      referenceAttributeDefinition.stringFormat,
                    )}
                </ValueLink>
              </RecordItem>
            ) : null,
          )}
        </RecordsList>
      </InfiniteScroll>
    </Main>
  );
};
